import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Paperclip, X } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog.jsx';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select.jsx';
import { Button } from '../ui/button.jsx';
import { Label } from '../ui/label.jsx';
import { Textarea } from '../ui/textarea.jsx';
import { submitSupportRequest, SUPPORT_REQUEST_TYPES } from '../../services/api';

/**
 * Raise a support request or a suggestion without leaving the app.
 *
 * Kept to one decision and one box. The subject is taken from the first line of the message
 * rather than asked for — a second field on a support form is one more thing between someone
 * with a problem and telling us about it.
 */

/**
 * The bucket accepts these and refuses everything else, so the same list has to be here or a
 * rejected upload reads as a mysterious failure. Must match `allowedFileExtensions` on
 * `support-attachments` in appwrite.config.json.
 */
const ACCEPTED = '.jpg,.jpeg,.png,.gif,.webp,.heic,.pdf';
/** Matches `maximumFileSize` on the same bucket. */
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 5;

export default function SupportRequestDialog({ open, onOpenChange }) {
  const [type, setType] = useState('support');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setType('support');
      setMessage('');
      setFiles([]);
      setError('');
    }
  }, [open]);

  const addFiles = (event) => {
    const chosen = Array.from(event.target.files || []);
    // Reset the input so choosing the same file twice in a row still fires a change event.
    event.target.value = '';

    const tooBig = chosen.filter((file) => file.size > MAX_FILE_BYTES);
    if (tooBig.length > 0) {
      setError(`${tooBig[0].name} is larger than 10 MB. Screenshots are usually well under that.`);
      return;
    }
    if (files.length + chosen.length > MAX_FILES) {
      setError(`Up to ${MAX_FILES} files can be attached to one request.`);
      return;
    }
    setError('');
    setFiles((previous) => [...previous, ...chosen]);
  };

  const removeFile = (index) => setFiles((previous) => previous.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Tell us what is happening and we will pick it up from here.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const { reference } = await submitSupportRequest({ type, message, files });
      toast.success(`Request ${reference} raised`, {
        description: 'You can follow it under Support in the sidebar.',
      });
      onOpenChange(false);
    } catch (submitError) {
      // Left on screen rather than toasted: whatever they typed is still in the box, and a
      // toast that disappears takes the only explanation of why it is still there with it.
      setError(submitError?.message || 'That request could not be sent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Get support</DialogTitle>
          <DialogDescription>
            Tell us what is going on. We will see which part of the app you are using, so there
            is no need to describe your setup.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="support-type">Request type</Label>
            <Select value={type} onValueChange={setType} disabled={submitting}>
              <SelectTrigger id="support-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORT_REQUEST_TYPES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="support-message">Message</Label>
            <Textarea
              id="support-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="What happened, and what were you trying to do?"
              className="min-h-[140px]"
              disabled={submitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="support-files">Screenshots (optional)</Label>
            <input
              id="support-files"
              type="file"
              multiple
              accept={ACCEPTED}
              onChange={addFiles}
              disabled={submitting}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-secondary-foreground hover:file:bg-secondary/80 disabled:cursor-not-allowed disabled:opacity-50"
            />

            {files.length > 0 && (
              <ul className="space-y-1">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-sm"
                  >
                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={submitting}
                      aria-label={`Remove ${file.name}`}
                      className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !message.trim()}>
            {submitting ? 'Sending…' : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
